import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useDialysisAdmission, useDialysisPrescription } from './dialysis.queries';
import { formatDialysisDate, formatDialysisDateTime } from './dialysis.utils';

const PRESCRIPTION_HEADER_NAME = 'opd_prescription';

type ContentSection = { title: string; body: string; html?: boolean };

export const DialysisPrescriptionPrintPage = () => {
  const { admissionId } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') === '1';
  const hospitalTitle = useHospitalTitle();
  const admissionQuery = useDialysisAdmission(admissionId);
  const prescriptionQuery = useDialysisPrescription(admissionId);
  const headerLogoQuery = useDocumentHeaderLogo(PRESCRIPTION_HEADER_NAME, withHeader);

  useEffect(() => {
    if (admissionQuery.data && prescriptionQuery.data !== undefined && (!withHeader || headerLogoQuery.isFetched)) window.setTimeout(() => window.print(), 300);
  }, [admissionQuery.data, prescriptionQuery.data, withHeader, headerLogoQuery.isFetched]);

  if (admissionQuery.isLoading || prescriptionQuery.isLoading) return <LoadingState />;
  if (admissionQuery.isError || !admissionQuery.data) return <ErrorState title="Admission not found" message="The admission could not be loaded." />;
  if (!prescriptionQuery.data) return <EmptyState title="No prescription yet" message="A prescription has not been created for this admission. Use Create E-Prescription to create one first." />;

  const admission = admissionQuery.data;
  const prescription = prescriptionQuery.data;
  const patient = admission.patientSnapshot;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const headerLogo = withHeader ? headerLogoQuery.data : null;

  const sections: ContentSection[] = [
    {
      title: 'Vitals',
      body: prescription.vitals.map((row) => [
        `Height: ${row.height ?? '-'}`,
        `Weight: ${row.weight ?? '-'}`,
        `Pulse: ${row.pulse ?? '-'}`,
        `BP: ${row.bpSystolic ?? '-'}/${row.bpDiastolic ?? '-'}`,
        `Temperature: ${row.temperature ?? '-'}`,
      ].join(' · ')).join('\n'),
    },
    {
      title: 'Complaints',
      body: prescription.complaints.map((row) =>
        `${formatDialysisDate(row.date)} — ${row.complaint ?? '-'} (Frequency: ${row.frequency ?? '-'}, Severity: ${row.severity ?? '-'}, Duration: ${row.duration ?? '-'})`).join('\n'),
    },
    {
      title: 'Diagnosis',
      body: prescription.diagnoses.map((row) =>
        `${formatDialysisDate(row.date)} — ${row.diagnosis ?? '-'} (Duration: ${row.duration ?? '-'})`).join('\n'),
    },
    {
      title: 'Medicines',
      body: prescription.medicines.map((row) =>
        `${row.medicine ?? '-'}${row.composition ? ` (${row.composition})` : ''} — ${row.dose ?? '-'}, ${row.when ?? '-'}, ${row.frequency ?? '-'}, ${row.duration ?? '-'}${row.notes ? `. ${row.notes}` : ''}`).join('\n'),
    },
    ...(prescription.testsNeeded ? [{ title: 'Tests Needed', body: prescription.testsNeeded }] : []),
    ...(prescription.advice ? [{ title: 'Advice', body: prescription.advice, html: true }] : []),
  ].filter((section) => section.body.trim().length > 0);

  return (
    <div className="print-discharge-summary">
      {withHeader ? (
        headerLogo ? (
          <div className="print-discharge-summary-logo"><img src={headerLogo} alt={hospitalTitle} /></div>
        ) : (
          <div className="print-discharge-summary-title-fallback">{hospitalTitle}</div>
        )
      ) : null}

      <h1 className="print-discharge-summary-heading">PRESCRIPTION</h1>

      <table className="print-discharge-summary-header">
        <tbody>
          <tr>
            <td>
              <p><b>Patient&apos;s Name : </b>{patient.name}</p>
              <p><b>UHID : </b>{patient.uhid ?? '-'}</p>
              <p><b>Mobile No : </b>{patient.phone ?? '-'}</p>
              <p><b>Sex : </b>{patient.gender}, <b>Age : </b>{age}</p>
              {patient.guardianName ? <p><b>Guardian Name : </b>{patient.guardianName}</p> : null}
              <p><b>Address : </b>{address}</p>
            </td>
            <td>
              <p><b>Dialysis No : </b>{admission.dialysisNumber}</p>
              <p><b>Admission Date : </b>{formatDialysisDateTime(admission.admissionDate)}</p>
              <p><b>Patient Type : </b>{admission.insuranceType}</p>
              <p><b>Bed : </b>{admission.bed.name} <b>Ward : </b>{admission.bedCategory.name}</p>
              <p><b>Under Doctor : </b>{doctorNameOrDash(admission.underDoctor?.name)}</p>
              <p><b>Prescription Date : </b>{formatDialysisDateTime(prescription.prescriptionDate)}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="print-discharge-summary-content">
        {sections.map((section) => (
          <div className="print-discharge-summary-section" key={section.title}>
            <p className="print-discharge-summary-section-title"><span className="print-discharge-summary-bullet" aria-hidden="true" />{section.title}</p>
            {section.html
              ? <div className="print-discharge-summary-section-body" dangerouslySetInnerHTML={{ __html: section.body }} />
              : <p className="print-discharge-summary-section-body">{section.body}</p>}
          </div>
        ))}
      </div>

      <div className="print-discharge-summary-signoff">
        <span className="print-discharge-summary-signoff-line">Patient/Guardian Signature</span>
        <span className="print-discharge-summary-signoff-spacer" />
        <span className="print-discharge-summary-signoff-line">Signed by Doctor</span>
      </div>
    </div>
  );
};
